import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getClientBranding } from '@/services/Client/getClientBranding'
import {
  ClientBranding,
  ClientBrandingValues,
  ClientCountry,
} from '@/types/client'

export const CLIENT_UID_STORAGE_KEY = 'client_uid'

// Module-level cache keyed by client uid. Deliberately not React Query: this hook
// is called from PublicLayout, which wraps most pre-auth pages, and requiring a
// QueryClientProvider there would couple every one of them to it. Branding is a
// tiny immutable-ish payload, so a shared promise map gives the same de-duping.
const cache = new Map<string, ClientBranding>()
const inflight = new Map<string, Promise<ClientBranding>>()

const fetchBranding = (uid: string): Promise<ClientBranding> => {
  const cached = cache.get(uid)
  if (cached) return Promise.resolve(cached)

  const existing = inflight.get(uid)
  if (existing) return existing

  const request = getClientBranding(uid)
    .then((branding) => {
      cache.set(uid, branding)
      return branding
    })
    .finally(() => inflight.delete(uid))

  inflight.set(uid, request)
  return request
}

/**
 * Read the whitelabel `clientUid`, remembering the last one seen this session.
 *
 * Outreach links carry the uid, but the screen that most needs it — the sign-in
 * form — is usually reached *after* the param is gone: an expired magic link bounces
 * the user from /auth/verify to /login with a bare URL. sessionStorage is the right
 * scope for that. It survives the bounce within the tab, while a fresh tab starts
 * unattributed rather than inheriting someone else's brand.
 */
export const readStoredClientUid = (): string | null => {
  try {
    return sessionStorage.getItem(CLIENT_UID_STORAGE_KEY)
  } catch {
    // Private-mode Safari can throw on storage access; unbranded is a fine fallback.
    return null
  }
}

type UseClientBrandingResult = {
  clientUid: string | null
  /** Present whenever the client resolved — enough on its own to identify them. */
  name: string | null
  /** What they configured; individual fields are null when unset. */
  branding: ClientBrandingValues | null
  /** Jurisdiction whose legals apply to this client; null when unattributed. */
  country: ClientCountry | null
  isLoading: boolean
}

export const useClientBranding = (): UseClientBrandingResult => {
  const [searchParams] = useSearchParams()
  const paramUid = searchParams.get('clientUid')

  useEffect(() => {
    if (!paramUid) return
    try {
      sessionStorage.setItem(CLIENT_UID_STORAGE_KEY, paramUid)
    } catch {
      // Storage unavailable — branding just won't survive the next navigation.
    }
  }, [paramUid])

  // The param wins outright, so this render needs no help from the write above.
  const clientUid = paramUid ?? readStoredClientUid()
  const [resolved, setResolved] = useState<ClientBranding | null>(
    clientUid ? (cache.get(clientUid) ?? null) : null,
  )
  const [isLoading, setIsLoading] = useState(
    !!clientUid && !cache.has(clientUid),
  )

  useEffect(() => {
    if (!clientUid) {
      setResolved(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(!cache.has(clientUid))
    fetchBranding(clientUid)
      .then((result) => {
        if (!cancelled) setResolved(result)
      })
      .catch(() => {
        // An unknown or unreachable client just renders unbranded — never a
        // reason to block someone from signing in.
        if (!cancelled) setResolved(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clientUid])

  return {
    clientUid,
    name: resolved?.name ?? null,
    branding: resolved?.branding ?? null,
    country: resolved?.country ?? null,
    isLoading,
  }
}
